// 調整舞台面板的機關清單：照名字分組（純函式，有測試 mechGroups.test.ts）。
//   一排（groupId）→ 一組；名字只差編號的（旋轉屏-1 旋轉、旋轉屏-2 旋轉…，nameFamily）→ 一組；
//   名字只差左右的（側面 LED-左、側面 LED-右）→ 一組；吊掛 → 一組；都不是 → 一個單獨的機關。
// 每組一個標題，組內用小方塊：有編號寫編號（1 2 3），左右寫左 / 右。

import type { Mechanism } from '../mechanisms/model';
import { groupLabel } from './panelText';
import { isVerticalAxis, nameFamily } from './spin';
import { hoistsOfRigDefs, membersOfGroup } from './scope';

export type PanelDef = Pick<Mechanism, 'id' | 'name' | 'kind' | 'axis'> & Partial<Pick<Mechanism, 'variant' | 'groupId' | 'groupIndex' | 'rigId' | 'rigIndex'>>;

export interface PanelTile<T> { def: T; tile: string }

export type PanelGroup<T> =
  /** groupId = 一排機關才有（可以「整排一起」動把手和滑桿）；名字分出來的組沒有 */
  | { type: 'row'; key: string; title: string; groupId: string | null; members: PanelTile<T>[] }
  | { type: 'rig'; key: string; title: string; rig: T; hoists: T[] }
  | { type: 'single'; key: string; title: string; def: T };

export type MechShape = 'spin' | 'flip' | 'slide' | 'lift' | 'arc' | 'hoist';

/** 機關是哪一種（面板用哪個圖示） */
export function mechShape(def: Pick<PanelDef, 'kind' | 'axis' | 'variant'>): MechShape {
  if (def.variant === 'hoist' || def.variant === 'hoist_rig') return 'hoist';
  if (def.variant === 'arc') return 'arc';
  if (def.kind === 'rotary') return isVerticalAxis(def.axis.y) ? 'spin' : 'flip';
  return isVerticalAxis(def.axis.y) ? 'lift' : 'slide';
}

const SIDE_ORDER = ['左', '中', '右', '前', '後', '上', '下'];
const SIDE_END = /^(.*\S)[\s\-_]*(左|右|中|前|後|上|下)$/u;

/** 名字只差左右（「側面 LED-左」→ { base: 側面 LED, side: 左 }）；不是 → null */
export function sideParts(name: string): { base: string; side: string } | null {
  const m = name.trim().match(SIDE_END);
  if (!m) return null;
  const base = m[1].replace(/[\s\-_]+$/u, '');
  return base ? { base, side: m[2] } : null;
}

/** 「旋轉屏-# 旋轉」→「旋轉屏 旋轉」 */
export function familyTitle(family: string): string {
  return family.replace(/[\s\-_]*#+[\s\-_]*/gu, ' ').trim();
}

const numbersOf = (name: string): string[] => name.match(/\d+/g) ?? [];

/** 組內每個小方塊上的字：大家編號位置一樣 → 不一樣的那個編號；不然 → 名字不一樣的那一段（太長就用第幾個） */
export function tileLabels(names: string[]): string[] {
  const nums = names.map(numbersOf);
  const len = nums[0]?.length ?? 0;
  if (len > 0 && nums.every(n => n.length === len)) {
    for (let i = 0; i < len; i++) {
      const col = nums.map(n => String(Number(n[i])));
      if (new Set(col).size === names.length) return col;
    }
  }
  let pre = names[0] ?? '';
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < pre.length && i < n.length && pre[i] === n[i]) i++;
    pre = pre.slice(0, i);
  }
  const rest = names.map(n => n.slice(pre.length).trim());
  if (rest.every(r => r.length > 0 && r.length <= 3) && new Set(rest).size === names.length) return rest;
  return names.map((_, i) => String(i + 1));
}

const byNumber = <T,>(list: PanelTile<T>[]): PanelTile<T>[] => {
  const num = (t: string) => { const n = Number(t); return Number.isFinite(n) ? n : null; };
  if (list.every(x => num(x.tile) !== null)) return [...list].sort((a, b) => num(a.tile)! - num(b.tile)!);
  const side = (t: string) => { const i = SIDE_ORDER.indexOf(t); return i < 0 ? 99 : i; };
  return [...list].sort((a, b) => side(a.tile) - side(b.tile));
};

/**
 * 機關清單 → 分組（順序照第一個成員出現的位置）。
 * show = 這個機關在清單上看得到嗎（沒帶物件的 3D 上點不到，不列）。
 */
export function panelGroups<T extends PanelDef>(defs: T[], show: (d: T) => boolean): PanelGroup<T>[] {
  const rigIds = new Set(defs.filter(d => d.variant === 'hoist_rig').map(d => d.id));
  const shown = defs.filter(d => {
    if (d.variant === 'hoist' && d.rigId && rigIds.has(d.rigId)) return false;
    return show(d);
  });
  // 先把名字分出來的組算好：成員至少兩個才算一組
  const famOf = (d: T): string | null => {
    if (d.variant === 'hoist_rig' || d.groupId) return null;
    const fam = nameFamily(d.name);
    if (fam) return `${d.kind}|${fam}`;
    const side = sideParts(d.name);
    return side ? `${d.kind}|${side.base}|side` : null;
  };
  const fams = new Map<string, T[]>();
  for (const d of shown) {
    const f = famOf(d);
    if (f) fams.set(f, [...(fams.get(f) ?? []), d]);
  }
  const out: PanelGroup<T>[] = [];
  const done = new Set<string>();
  for (const d of shown) {
    if (d.variant === 'hoist_rig') {
      out.push({ type: 'rig', key: `rig:${d.id}`, title: d.name, rig: d, hoists: hoistsOfRigDefs(defs, d.id) });
      continue;
    }
    if (d.groupId) {
      if (done.has(`g:${d.groupId}`)) continue;
      done.add(`g:${d.groupId}`);
      const members = membersOfGroup(defs, d.groupId).filter(m => shown.includes(m));
      if (members.length > 1) {
        const tiles = tileLabels(members.map(m => m.name));
        out.push({ type: 'row', key: `g:${d.groupId}`, title: groupLabel(members.map(m => m.name)), groupId: d.groupId, members: members.map((def, i) => ({ def, tile: tiles[i] })) });
        continue;
      }
      out.push({ type: 'single', key: `s:${d.id}`, title: d.name, def: d });
      continue;
    }
    const f = famOf(d);
    const members = f ? fams.get(f)! : [];
    if (f && members.length > 1) {
      if (done.has(`f:${f}`)) continue;
      done.add(`f:${f}`);
      const isSide = f.endsWith('|side');
      const tiles = isSide ? members.map(m => sideParts(m.name)!.side) : tileLabels(members.map(m => m.name));
      const title = isSide ? sideParts(d.name)!.base : familyTitle(nameFamily(d.name)!) || groupLabel(members.map(m => m.name));
      out.push({ type: 'row', key: `f:${f}`, title, groupId: null, members: byNumber(members.map((def, i) => ({ def, tile: tiles[i] }))) });
      continue;
    }
    out.push({ type: 'single', key: `s:${d.id}`, title: d.name, def: d });
  }
  return out;
}

/** 清單上點的順序（Shift 連選用）：一個一個機關，吊掛只算整組 */
export function panelOrder<T extends PanelDef>(groups: PanelGroup<T>[]): string[] {
  return groups.flatMap(g => (g.type === 'single' ? [g.def.id] : g.type === 'rig' ? [g.rig.id] : g.members.map(m => m.def.id)));
}

/** 選到的剛好是整組 → 那一組（面板標題寫組名、不寫「選了 N 個」）；不是 → null */
export function exactGroup<T extends PanelDef>(groups: PanelGroup<T>[], picked: readonly string[]): Extract<PanelGroup<T>, { type: 'row' }> | null {
  const set = new Set(picked);
  for (const g of groups) {
    if (g.type === 'row' && g.members.length === set.size && g.members.every(m => set.has(m.def.id))) return g;
  }
  return null;
}
